/**
 * Cloud Functions Service
 * Wrapper for Firebase callable Cloud Functions.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from './firebase';

const functions = getFunctions(app);

/**
 * Analyze a document to determine type and estimate credits.
 * @param {string} documentId - Document ID to analyze
 * @returns {Promise<{success: boolean, analysis?: object, error?: string}>}
 */
export const analyzeDocument = async (documentId, password = null) => {
  try {
    const callable = httpsCallable(functions, 'analyzeDocument');
    const payload = { documentId };
    if (password) payload.password = password;
    const result = await callable(payload);
    return result.data;
  } catch (error) {
    console.error('analyzeDocument error:', error);

    // Extract user-friendly error message
    let errorMessage = 'Failed to analyze document';
    if (error.code === 'unauthenticated') {
      errorMessage = 'You must be logged in to analyze documents';
    } else if (error.code === 'permission-denied') {
      errorMessage = 'You do not have permission to access this document';
    } else if (error.code === 'not-found') {
      errorMessage = 'Document not found';
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Process a document for PII detection.
 * @param {string} documentId - Document ID to process
 * @param {string} domainType - Optional domain type override
 * @returns {Promise<{success: boolean, piiCount?: number, creditsUsed?: number, error?: string}>}
 */
export const processDocument = async (documentId, domainType = null) => {
  try {
    const callable = httpsCallable(functions, 'processDocument');
    const payload = { documentId };
    if (domainType) {
      payload.domainType = domainType;
    }
    const result = await callable(payload);
    return result.data;
  } catch (error) {
    console.error('processDocument error:', error);

    // Extract user-friendly error message
    let errorMessage = 'Failed to process document';
    if (error.code === 'unauthenticated') {
      errorMessage = 'You must be logged in to process documents';
    } else if (error.code === 'permission-denied') {
      errorMessage = 'You do not have permission to access this document';
    } else if (error.code === 'not-found') {
      errorMessage = 'Document not found';
    } else if (error.code === 'failed-precondition') {
      // This includes insufficient credits
      errorMessage = error.message || 'Processing requirements not met';
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Generate a customized redacted PDF based on user settings.
 * @param {string} documentId - Document ID to generate redacted PDF for
 * @param {object} settings - Redaction settings
 * @param {string} settings.source - 'redacted' or 'original'
 * @param {string} settings.redactionMode - 'redact' or 'highlight'
 * @param {string} settings.redactionColor - Hex color (e.g., '#000000')
 * @param {boolean} settings.includeLog - Whether to include CSV redaction log
 * @returns {Promise<{success: boolean, pdfUrl?: string, logUrl?: string, fileName?: string, error?: string}>}
 */
export const generateCustomRedactedPdf = async (documentId, settings) => {
  try {
    const callable = httpsCallable(functions, 'generateCustomRedactedPdf');
    const result = await callable({ documentId, settings });
    return result.data;
  } catch (error) {
    console.error('generateCustomRedactedPdf error:', error);

    // Extract user-friendly error message
    let errorMessage = 'Failed to generate redacted PDF';
    if (error.code === 'unauthenticated') {
      errorMessage = 'You must be logged in to download documents';
    } else if (error.code === 'permission-denied') {
      errorMessage = 'You do not have permission to access this document';
    } else if (error.code === 'not-found') {
      errorMessage = 'Document not found';
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Update PII item status (e.g., mark as approved/ignored).
 * @param {string} piiItemId - PII item ID to update
 * @param {string} status - New status ('detected', 'approved', 'redacted')
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updatePiiItemStatus = async (piiItemId, status) => {
  try {
    const callable = httpsCallable(functions, 'updatePiiItemStatus');
    const result = await callable({ piiItemId, status });
    return result.data;
  } catch (error) {
    console.error('updatePiiItemStatus error:', error);

    // Extract user-friendly error message
    let errorMessage = 'Failed to update PII item status';
    if (error.code === 'unauthenticated') {
      errorMessage = 'You must be logged in';
    } else if (error.code === 'permission-denied') {
      errorMessage = 'You do not have permission to modify this item';
    } else if (error.code === 'not-found') {
      errorMessage = 'PII item not found';
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Batch-update status for multiple PII items in a single Firestore write.
 * @param {Array<{piiItemId: string, status: string}>} items
 * @returns {Promise<{success: boolean, updatedCount?: number, error?: string}>}
 */
export const updatePiiItemsBatch = async (items) => {
  try {
    const callable = httpsCallable(functions, 'updatePiiItemsBatch');
    const result = await callable({ items });
    return result.data;
  } catch (error) {
    console.error('updatePiiItemsBatch error:', error);

    let errorMessage = 'Failed to update PII items';
    if (error.code === 'unauthenticated') {
      errorMessage = 'You must be logged in';
    } else if (error.code === 'permission-denied') {
      errorMessage = 'You do not have permission to modify these items';
    } else if (error.code === 'not-found') {
      errorMessage = 'One or more PII items not found';
    } else if (error.code === 'invalid-argument') {
      errorMessage = error.message || 'Invalid request';
    }

    return { success: false, error: errorMessage };
  }
};

/**
 * Search for keyword in document and create PII redactions.
 * @param {string} documentId - Document ID
 * @param {object} params - Search parameters
 * @param {string} params.keyword - Keyword to search for
 * @param {string} params.category - PII category to assign
 * @param {string} params.label - Optional custom label
 * @returns {Promise<{success: boolean, count?: number, error?: string}>}
 */
export const searchPiiInDocument = async (documentId, params) => {
  try {
    const callable = httpsCallable(functions, 'searchPiiInDocument');
    const result = await callable({ documentId, ...params });
    return result.data;
  } catch (error) {
    console.error('searchPiiInDocument error:', error);

    let errorMessage = 'Failed to search document';
    if (error.code === 'unauthenticated') {
      errorMessage = 'You must be logged in';
    } else if (error.code === 'permission-denied') {
      errorMessage = 'You do not have permission to access this document';
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Delete a document and all associated resources (Storage files, PII items).
 * @param {string} documentId - Document ID to delete
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteDocumentComplete = async (documentId) => {
  try {
    const callable = httpsCallable(functions, 'deleteDocumentComplete');
    const result = await callable({ documentId });
    return result.data;
  } catch (error) {
    console.error('deleteDocumentComplete error:', error);

    let errorMessage = 'Failed to delete document';
    if (error.code === 'unauthenticated') {
      errorMessage = 'You must be logged in to delete documents';
    } else if (error.code === 'permission-denied') {
      errorMessage = 'You do not have permission to delete this document';
    } else if (error.code === 'not-found') {
      errorMessage = 'Document not found';
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Run Categories detection on a text-based PDF document.
 * Uses regex (8 categories) + Claude Haiku (7 categories).
 * @param {string} documentId - Document ID to process
 * @returns {Promise<{success: boolean, itemCount?: number, creditsUsed?: number, cached?: boolean, categorySummary?: object, error?: string}>}
 */
export const processCategoriesDetection = async (documentId) => {
  try {
    const callable = httpsCallable(functions, 'processCategoriesDetection');
    const result = await callable({ documentId });
    return result.data;
  } catch (error) {
    console.error('processCategoriesDetection error:', error);

    let errorMessage = 'Failed to run categories detection';
    if (error.code === 'unauthenticated') {
      errorMessage = 'You must be logged in';
    } else if (error.code === 'permission-denied') {
      errorMessage = 'You do not have permission to access this document';
    } else if (error.code === 'not-found') {
      errorMessage = 'Document not found';
    } else if (error.code === 'failed-precondition') {
      errorMessage = error.message || 'Requirements not met';
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Add manual PII redaction box.
 * @param {string} documentId - Document ID
 * @param {object} params - Redaction parameters
 * @param {number} params.page - Page number (1-indexed)
 * @param {object} params.boundingBox - Bounding box {x0, y0, x1, y1} (normalized 0-1)
 * @param {string} params.category - PII category
 * @param {string} params.label - Optional custom label
 * @returns {Promise<{success: boolean, piiItemId?: string, error?: string}>}
 */
export const addManualPiiRedaction = async (documentId, params) => {
  try {
    const callable = httpsCallable(functions, 'addManualPiiRedaction');
    const result = await callable({ documentId, ...params });
    return result.data;
  } catch (error) {
    console.error('addManualPiiRedaction error:', error);

    let errorMessage = 'Failed to add manual redaction';
    if (error.code === 'unauthenticated') {
      errorMessage = 'You must be logged in';
    } else if (error.code === 'permission-denied') {
      errorMessage = 'You do not have permission to access this document';
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};
